import type {
  BorderRadius,
  Colors,
  FontSize,
  FontWeight,
  LineHeight,
  Spacing,
  UIKitProviderTheme,
} from "@qws-org/ui-kit";

import type {
  BorderRadiusCore,
  ColorsCore,
  FontSizeCore,
  FontWeightCore,
  LineHeightCore,
} from "~/core";

export const createTheme = (
  currentLang: LanguageCode,
  entry?: {
    fontSize?: FontSizeCore;
    lineHeight?: LineHeightCore;
    fontWeight?: FontWeightCore;
    fontFamily?: {
      main_name?: string | null;
      additional_name?: string | null;
      accent_name?: string | null;
    };
    colors?: ColorsCore;
    borderRadius?: BorderRadiusCore;
  },
): UIKitProviderTheme => {
  const fontSize: FontSize = {
    "headline.desktop.large": entry?.fontSize?.desktop_large_headline ?? 48,
    "headline.desktop.medium": entry?.fontSize?.desktop_medium_headline ?? 42,
    "headline.mobile.large": entry?.fontSize?.mobile_large_headline ?? 24,
    "headline.mobile.medium": entry?.fontSize?.mobile_medium_headline ?? 22,

    "title.desktop.large": entry?.fontSize?.desktop_large_title ?? 48,
    "title.desktop.medium": entry?.fontSize?.desktop_medium_title ?? 36,
    "title.desktop.small": entry?.fontSize?.desktop_small_title ?? 22,
    "title.desktop.xsmall": entry?.fontSize?.desktop_xsmall_title ?? 16,

    "title.mobile.large": entry?.fontSize?.mobile_large_title ?? 28,
    "title.mobile.medium": entry?.fontSize?.mobile_medium_title ?? 22,
    "title.mobile.small": entry?.fontSize?.mobile_small_title ?? 20,
    "title.mobile.xsmall": entry?.fontSize?.mobile_xsmall_title ?? 14,

    "body.desktop.large": entry?.fontSize?.desktop_large_body ?? 18,
    "body.desktop.medium": entry?.fontSize?.desktop_medium_body ?? 16,
    "body.desktop.small": entry?.fontSize?.desktop_small_body ?? 14,
    "body.desktop.xsmall": entry?.fontSize?.desktop_xsmall_body ?? 10,

    "body.mobile.large": entry?.fontSize?.mobile_large_body ?? 16,
    "body.mobile.medium": entry?.fontSize?.mobile_medium_body ?? 14,
    "body.mobile.small": entry?.fontSize?.mobile_small_body ?? 12,
    "body.mobile.xsmall": entry?.fontSize?.mobile_xsmall_body ?? 10,

    "button.desktop.large": entry?.fontSize?.desktop_large_button ?? 22,
    "button.desktop.medium": entry?.fontSize?.desktop_medium_button ?? 20,
    "button.desktop.small": entry?.fontSize?.desktop_small_button ?? 16,
    "button.desktop.xsmall": entry?.fontSize?.desktop_xsmall_button ?? 12,

    "button.mobile.large": entry?.fontSize?.mobile_large_button ?? 22,
    "button.mobile.medium": entry?.fontSize?.mobile_medium_button ?? 20,
    "button.mobile.small": entry?.fontSize?.mobile_small_button ?? 16,
    "button.mobile.xsmall": entry?.fontSize?.mobile_xsmall_button ?? 12,
  };
  const lineHeight: LineHeight = {
    "headline.desktop.large": entry?.lineHeight?.desktop_large_headline ?? 48,
    "headline.desktop.medium": entry?.lineHeight?.desktop_medium_headline ?? 42,
    "headline.mobile.large": entry?.lineHeight?.mobile_large_headline ?? 24,
    "headline.mobile.medium": entry?.lineHeight?.mobile_medium_headline ?? 22,

    "title.desktop.large": entry?.lineHeight?.desktop_large_title ?? 64,
    "title.desktop.medium": entry?.lineHeight?.desktop_medium_title ?? 48,
    "title.desktop.small": entry?.lineHeight?.desktop_small_title ?? 32,
    "title.desktop.xsmall": entry?.lineHeight?.desktop_xsmall_title ?? 20,

    "title.mobile.large": entry?.lineHeight?.mobile_large_title ?? 36,
    "title.mobile.medium": entry?.lineHeight?.mobile_medium_title ?? 30,
    "title.mobile.small": entry?.lineHeight?.mobile_small_title ?? 28,
    "title.mobile.xsmall": entry?.lineHeight?.mobile_xsmall_title ?? 18,

    "body.desktop.large": entry?.lineHeight?.desktop_large_body ?? 24,
    "body.desktop.medium": entry?.lineHeight?.desktop_medium_body ?? 20,
    "body.desktop.small": entry?.lineHeight?.desktop_small_body ?? 18,
    "body.desktop.xsmall": entry?.lineHeight?.desktop_small_body ?? 16,

    "body.mobile.large": entry?.lineHeight?.mobile_large_body ?? 20,
    "body.mobile.medium": entry?.lineHeight?.mobile_medium_body ?? 18,
    "body.mobile.small": entry?.lineHeight?.mobile_small_body ?? 16,
    "body.mobile.xsmall": entry?.lineHeight?.mobile_small_body ?? 14,

    "button.desktop.large": entry?.lineHeight?.desktop_large_button ?? 34,
    "button.desktop.medium": entry?.lineHeight?.desktop_medium_button ?? 28,
    "button.desktop.small": entry?.lineHeight?.desktop_small_button ?? 24,
    "button.desktop.xsmall": entry?.lineHeight?.desktop_xsmall_button ?? 16,

    "button.mobile.large": entry?.lineHeight?.mobile_large_button ?? 34,
    "button.mobile.medium": entry?.lineHeight?.mobile_medium_button ?? 28,
    "button.mobile.small": entry?.lineHeight?.mobile_small_button ?? 24,
    "button.mobile.xsmall": entry?.lineHeight?.mobile_xsmall_button ?? 16,
  };
  const fontWeight: FontWeight = {
    "desktop.superbold": entry?.fontWeight?.desktop_superbold ?? 900,
    "desktop.bold": entry?.fontWeight?.desktop_bold ?? 700,
    "desktop.semibold": entry?.fontWeight?.desktop_semibold ?? 600,
    "desktop.normal": entry?.fontWeight?.desktop_normal ?? 400,
    "desktop.light": entry?.fontWeight?.desktop_light ?? 100,

    "mobile.superbold": entry?.fontWeight?.mobile_superbold ?? 900,
    "mobile.bold": entry?.fontWeight?.mobile_bold ?? 700,
    "mobile.semibold": entry?.fontWeight?.mobile_semibold ?? 600,
    "mobile.normal": entry?.fontWeight?.mobile_normal ?? 400,
    "mobile.light": entry?.fontWeight?.mobile_light ?? 100,
  };
  const spacing: Spacing = {
    0: "0px",
    2: "2px",
    4: "4px",
    8: "8px",
    12: "12px",
    16: "16px",
    20: "20px",
    24: "24px",
    32: "32px",
    40: "40px",
    48: "48px",
    56: "56px",
    64: "64px",
    80: "80px",
    96: "96px",
    auto: "auto",
  };
  const colors: Colors = {
    // background
    "background-primary": entry?.colors?.background_primary ?? "#000000",
    "background-secondary": entry?.colors?.background_secondary ?? "#171717",
    "background-tetriary": entry?.colors?.background_tertiary ?? "#19171F",
    "background-secondary-transparent":
      entry?.colors?.background_secondary_transparent ??
      "rgba(23, 23, 23, 0.7)",

    "background-accent-vip":
      entry?.colors?.background_accent_vip ??
      "linear-gradient(270deg, #AE6E1D 0%, #FFDA8A 62.74%, #C8842E 100%)",
    "background-transparent":
      entry?.colors?.background_transparent ?? "#00000080",
    "background-accent1":
      entry?.colors?.background_accent1 ??
      "linear-gradient(270deg, #F4AD23 0%, #FF3D00 50%, #6A2DC0 100%)",
    "background-accent1-vertical":
      entry?.colors?.background_accent1_vertical ??
      "linear-gradient(135deg, #FBBD13 0%, #FF3E1C 50%, #6A2DC0 100%)",
    "background-accent2":
      entry?.colors?.background_accent2 ??
      "linear-gradient(90deg, #FFC169 0%, #FFFAEA 25%, #FBA2C6 42%, #F057A2 55%, #985BE7 70%, #60ABED 85%, #50E6C1 100%)",

    "background-accent2-reversed":
      entry?.colors?.background_accent2_reversed ??
      "linear-gradient(90deg, #50E6C1 0%, #60ABED 20%, #985BE7 40%, #F057A2 60%, #FFB6A9 80%, #FFB957 100%)",

    "background-accent2-vertical":
      entry?.colors?.background_accent2_vertical ??
      "linear-gradient(144.58deg, #FFFAEA 1.33%, #F057A2 33.89%, #985BE7 66.45%, #60ABED 100%)",
    "background-popups1": entry?.colors?.background_popups1 ?? "#271D34",
    "background-popups2": entry?.colors?.background_popups2 ?? "#322544",

    // border
    "border-accent": entry?.colors?.border_accent ?? "#F9D48C",
    "border-destructive": entry?.colors?.border_destructive ?? "#DD4952",
    "border-primary": entry?.colors?.border_primary ?? "#FFFFFF",
    "border-selected": entry?.colors?.border_selected ?? "#C49BFC",
    "border-subtle": entry?.colors?.border_subtle ?? "#FFFFFF33",
    "border-button": entry?.colors?.border_button ?? "#FFFFFF",
    "border-secondary": entry?.colors?.border_secondary ?? "#FFFFFF80",

    // text
    "text-accent-default": entry?.colors?.text_accent_default ?? "#F6BA46",
    "text-accent-hovered": entry?.colors?.text_accent_hovered ?? "#F9D48C",
    "text-accent-grad":
      entry?.colors?.text_accent_grad ??
      "linear-gradient(180deg, #FBE1B0 0%, #F6BA46 100%)",
    "text-destructive": entry?.colors?.text_destructive ?? "#DD4952",
    "text-disabled": entry?.colors?.text_disabled ?? "#FFFFFF33",
    "text-selection": entry?.colors?.text_selection ?? "#C49BFC",
    "text-success": entry?.colors?.text_success ?? "#66C95B",
    "text-primary-default": entry?.colors?.text_primary_default ?? "#FFFFFF",
    "text-primary-hovered": entry?.colors?.text_primary_hovered ?? "#A66BFA",
    "text-primary-inverted": entry?.colors?.text_primary_inverted ?? "#000000",
    "text-primary-muted": entry?.colors?.text_primary_muted ?? "#FFFFFFB3",
    "text-secondary-default":
      entry?.colors?.text_secondary_default ?? "#FFFFFF80",
    "text-secondary-hovered":
      entry?.colors?.text_secondary_hovered ?? "#FFFFFF",

    // surface

    "surface-destructive-default":
      entry?.colors?.surface_destructive_default ?? "#D92933",
    "surface-destructive-subtle":
      entry?.colors?.surface_destructive_subtle ?? "#D9293333",

    "surface-neutral-dark-default":
      entry?.colors?.surface_neutral_dark_default ?? "#00000066",
    "surface-neutral-dark-hovered":
      entry?.colors?.surface_neutral_dark_hovered ?? "#000000B3",
    "surface-neutral-default":
      entry?.colors?.surface_neutral_default ?? "#282531",
    "surface-neutral-hovered":
      entry?.colors?.surface_neutral_hovered ?? "#FFFFFF33",
    "surface-neutral-light":
      entry?.colors?.surface_neutral_light ?? "#FFFFFF33",
    "surface-neutral-subtle":
      entry?.colors?.surface_neutral_subtle ?? "#FFFFFF08",

    "surface-primary-default":
      entry?.colors?.surface_primary_default ?? "#893BF7",
    "surface-primary-dark": entry?.colors?.surface_primary_dark ?? "#401A75",
    "surface-primary-hovered":
      entry?.colors?.surface_primary_hovered ?? "#9854F8",
    "surface-primary-subtle":
      entry?.colors?.surface_primary_subtle ?? "#893BF733",
    "surface-primary-grad-default":
      entry?.colors?.surface_primary_grad_default ??
      "linear-gradient(90deg, #6A2DC0 0%, #FF3D00 100%)",
    "surface-primary-grad-hovered":
      entry?.colors?.surface_primary_grad_hovered ??
      "linear-gradient(90deg, #FF3D00 0%, #893BF7 100%)",
    "surface-primary-grad-pressed":
      entry?.colors?.surface_primary_grad_pressed ??
      "linear-gradient(90deg, #A1210B 0%, #55239A 100%)",
    "surface-primary-grad-subtle":
      entry?.colors?.surface_primary_grad_subtle ??
      "linear-gradient(360deg, #2B1050 0%, rgba(43, 16, 80, 0.5) 100%)",

    "surface-tetriary-hovered":
      entry?.colors?.surface_tetriary_hovered ?? "#FFFFFF",

    "surface-secondary-default":
      entry?.colors?.surface_secondary_default ?? "#FFFFFF",
    "surface-secondary-hovered":
      entry?.colors?.surface_secondary_hovered ?? "#D4D4D4",

    "surface-accent1-default":
      entry?.colors?.surface_accent1_default ??
      "linear-gradient(270deg, #55239A 0%, #9854F8 50%, #55239A 100%)",
    "surface-accent2-default":
      entry?.colors?.surface_accent2_default ??
      "linear-gradient(90deg, #DCC3FE 0%, #FFCEA5 33%, #E3FAD6 66%, #CEFDFE 100%)",
    "surface-accent3-default":
      entry?.colors?.surface_accent3_default ??
      "linear-gradient(89.65deg, #F4AD23 0.3%, #F93448 75.03%)",
    "surface-accent1-hovered":
      entry?.colors?.surface_accent1_hovered ??
      "linear-gradient(270deg, #55239A 0%, #9854F8 50%, #55239A 100%),\n" +
        "linear-gradient(0deg,  rgba(0, 0, 0, 0.2),  rgba(0, 0, 0, 0.2)",
    "surface-accent2-hovered":
      entry?.colors?.surface_accent2_hovered ??
      "linear-gradient(90deg, #DCC3FE 0%, #FFCEA5 33%, #E3FAD6 66%, #CEFDFE 100%),\n" +
        "linear-gradient(0deg, (0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))\n",
    "surface-accent-button-default":
      entry?.colors?.surface_accent_button_default ??
      "linear-gradient(270deg, #7DE239 0%, #00C950 100%)",
    "surface-accent-button-hovered":
      entry?.colors?.surface_accent_button_hovered ??
      "linear-gradient(270deg, #00C950 0%, #7DE239 100%)",
    "surface-tag-cash": entry?.colors?.surface_tag_cash ?? "#00A6F4",
    "surface-tag-casino": entry?.colors?.surface_tag_casino ?? "#F6BA46",
    "surface-tag-mtt": entry?.colors?.surface_tag_mtt ?? "#F55C3D",
    "surface-tag-poker": entry?.colors?.surface_tag_poker ?? "#00A63E",
    "surface-tag-quests": entry?.colors?.surface_tag_quests ?? "#4F39F6",
    "surface-tag-social": entry?.colors?.surface_tag_social ?? "#CF3FFF",
    "surface-tag-timer": entry?.colors?.surface_tag_timer ?? "#F93448",
    "surface-tag-vip": entry?.colors?.surface_tag_vip ?? "#FFE849",
  };
  const borderRadius: BorderRadius = {
    banner: entry?.borderRadius?.banner ?? "12px",
    button: entry?.borderRadius?.button ?? "4px",
    "big-card": entry?.borderRadius?.big_card ?? "16px",
    card: entry?.borderRadius?.card ?? "8px",
    chip: entry?.borderRadius?.chip ?? "4px",
    checkbox: entry?.borderRadius?.checkbox ?? "2px",
    form: entry?.borderRadius?.form ?? "4px",
    menus: entry?.borderRadius?.menus ?? "8px",
    popups: entry?.borderRadius?.popups ?? "2px",
  };
  return {
    global:
      currentLang === "zh"
        ? { lineBreak: "auto", wordBreak: "auto-phrase" }
        : { lineBreak: "normal", wordBreak: "auto-phrase" },
    fontSize,
    lineHeight,
    fontWeight,
    fontFamily: {
      main_name: `"${entry?.fontFamily?.main_name ?? "Main"}", sans-serif`,
      additional_name: `"${entry?.fontFamily?.additional_name ?? "Additional"}", sans-serif`,
      accent_name: `"${entry?.fontFamily?.accent_name ?? "Accent"}", sans-serif`,
    },
    spacing,
    colors,
    borderRadius,
  };
};
